import { Box, Flex } from '@chakra-ui/react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { colors } from '../../theme/colors';
import Card from '../Dashboard/Card';
import CardHeader from '../Dashboard/CardHeader';

const SYMPTOM_COLORS: Record<string, string> = {
  depression: colors.deep_blue,
  anxiety: colors.warning,
  stress: colors.error,
  others: colors.dark_green
};

const capitalize = (s: string) =>
  s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : s;

interface SymptomsChartProps {
  data: any;
  isDownloading: boolean;
  handleChange: any;
  rangeValue?: string;
  startDate?: string;
  endDate?: string;
  onStartDateChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onEndDateChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const SymptomsChart = ({
  data,
  isDownloading,
  handleChange,
  rangeValue,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange
}: SymptomsChartProps) => {
  const chart = (
    <Card
      h='auto'
      w='100%'
      py='24px'
      bg={isDownloading ? colors.light_green : colors.white}
    >
      <Box width='100%'>
        <CardHeader
          title='Prevalent Symptoms'
          handleChange={handleChange}
          value={rangeValue}
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={onStartDateChange}
          onEndDateChange={onEndDateChange}
        />
        <Box borderRadius='12px' margin='auto' mt='16px'>
          <ResponsiveContainer width='100%' height={280}>
            <BarChart
              data={data}
              margin={{ top: 20, right: 20, left: 0, bottom: 5 }}
            >
              <CartesianGrid
                strokeDasharray='3 3'
                stroke='#E2E8F0'
                vertical={false}
              />
              <XAxis
                dataKey='name'
                strokeWidth={0}
                tick={{ fontSize: 12, fill: colors.gray }}
                tickFormatter={capitalize}
              />
              <YAxis
                strokeWidth={0}
                tick={{ fontSize: 12, fill: colors.gray }}
                allowDecimals={false}
              />
              <Tooltip
                cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                labelFormatter={(label: string) => capitalize(label)}
                formatter={(val: any) => [val, 'Cases']}
                contentStyle={{
                  backgroundColor: colors.white,
                  borderRadius: 8,
                  fontSize: '13px',
                  border: `1px solid ${colors.shade}`,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                }}
              />
              <Bar
                name='Prevalent Symptoms'
                dataKey='value'
                barSize={32}
                radius={[6, 6, 0, 0]}
                fill={colors.deep_blue}
                shape={(props: any) => {
                  const { x, y, width, height, payload } = props;
                  const barFill =
                    SYMPTOM_COLORS[payload?.name?.toLowerCase()] ||
                    colors.accent;
                  return (
                    <rect
                      x={x}
                      y={y}
                      width={width}
                      height={height}
                      rx={6}
                      ry={6}
                      fill={barFill}
                    />
                  );
                }}
              />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Box>
    </Card>
  );
  const symptomsChart = data.length ? (
    chart
  ) : (
    <Card h='auto' w='100%' py='24px'>
      <Box width='100%'>
        <CardHeader
          title='Prevalent Symptoms'
          handleChange={handleChange}
          value={rangeValue}
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={onStartDateChange}
          onEndDateChange={onEndDateChange}
        />
        <Flex
          justifyContent='center'
          marginX='auto'
          marginY='40px'
          width='100%'
          color={colors.gray}
        >
          {rangeValue === 'CUSTOM' && (!startDate || !endDate)
            ? 'Select start and end dates'
            : 'No data available'}
        </Flex>
      </Box>
    </Card>
  );
  return symptomsChart;
};

export default SymptomsChart;
